import type { ComputedRef, Ref } from 'vue'
import logger from '@/logger'
import { IntlService } from '@progress/kendo-vue-intl'

import enCaGregorian from 'cldr-dates-full/main/en/ca-gregorian.json'
import deCaGregorian from 'cldr-dates-full/main/de/ca-gregorian.json'
import esCaGregorian from 'cldr-dates-full/main/es/ca-gregorian.json'
import frCaGregorian from 'cldr-dates-full/main/fr/ca-gregorian.json'

import enDateFields from 'cldr-dates-full/main/en/dateFields.json'
import deDateFields from 'cldr-dates-full/main/de/dateFields.json'
import esDateFields from 'cldr-dates-full/main/es/dateFields.json'
import frDateFields from 'cldr-dates-full/main/fr/dateFields.json'

import enNumbers from 'cldr-numbers-full/main/en/numbers.json'
import deNumbers from 'cldr-numbers-full/main/de/numbers.json'
import esNumbers from 'cldr-numbers-full/main/es/numbers.json'
import frNumbers from 'cldr-numbers-full/main/fr/numbers.json'

import enCurrencies from 'cldr-numbers-full/main/en/currencies.json'
import deCurrencies from 'cldr-numbers-full/main/de/currencies.json'
import esCurrencies from 'cldr-numbers-full/main/es/currencies.json'
import frCurrencies from 'cldr-numbers-full/main/fr/currencies.json'

import likelySubtags from 'cldr-core/supplemental/likelySubtags.json'
import numberingSystems from 'cldr-core/supplemental/numberingSystems.json'
import currencyData from 'cldr-core/supplemental/currencyData.json'
import weekData from 'cldr-core/supplemental/weekData.json'
import timeData from 'cldr-core/supplemental/timeData.json'
import { load, loadMessages } from '@progress/kendo-vue-intl'

type LocaleLike = string | Ref<string> | ComputedRef<string>

function resolveLocale(locale: LocaleLike): string {
  return typeof locale === 'string' ? locale : locale.value
}

function normalizeLocaleString(locale: string): string {
  return locale.replace('_', '-').trim()
}

export function getKendoLanguage(locale: LocaleLike): string {
  const resolved = resolveLocale(locale)
  const raw = normalizeLocaleString(resolved)
  if (!raw) {
    const fallback = 'en-US'
    logger.debug('getKendoLanguage', { input: resolved, raw, result: fallback })
    return fallback
  }

  const parts = raw.split('-')
  const language = (parts[0] ?? '').toLowerCase()
  const region = parts[1]

  if (region) {
    const result = `${language}-${region.toUpperCase()}`
    logger.debug('getKendoLanguage', { input: resolved, raw, result })
    return result
  }

  const defaults: Record<string, string> = {
    en: 'en-US',
    de: 'de-DE',
    fr: 'fr-FR',
    es: 'es-ES',
  }

  const result = defaults[language] ?? 'en-US'
  logger.debug('getKendoLanguage', { input: resolved, raw, result })
  return result
}

export function getKendoLocale(locale: LocaleLike): string {
  const resolved = resolveLocale(locale)
  const raw = normalizeLocaleString(resolved)
  if (!raw) {
    const fallback = 'en'
    logger.debug('getKendoLocale', { input: resolved, raw, result: fallback })
    return fallback
  }

  const parts = raw.split('-')
  const language = (parts[0] ?? '').toLowerCase()
  const result = language || 'en'
  logger.debug('getKendoLocale', { input: resolved, raw, result })
  return result
}

export function loadLocaleData(i18nLocale: string) {
  logger.debug('loadLocaleData', { i18nLocale })
  const localeName = i18nLocale.split('-')[0]

  load(likelySubtags, numberingSystems, currencyData, weekData, timeData)

  if (localeName == 'en') {
    load(
      sanitizeNumbersCldr(enNumbers),
      enCurrencies,
      enCaGregorian,
      enDateFields,
    )
  } else if (localeName == 'de') {
    load(
      sanitizeNumbersCldr(deNumbers),
      deCurrencies,
      patchDateSeparator(deCaGregorian, '.', '/'),
      deDateFields,
    )
  } else if (localeName == 'es') {
    load(
      sanitizeNumbersCldr(esNumbers),
      esCurrencies,
      esCaGregorian,
      esDateFields,
    )
  } else if (localeName == 'fr') {
    load(
      sanitizeNumbersCldr(frNumbers),
      frCurrencies,
      frCaGregorian,
      frDateFields,
    )
  } else {
    logger.error('unsupported localeName:', localeName)
  }
}

function patchDateSeparator(cldrData: any, from: string, to: string): any {
  const locale = Object.keys(cldrData.main)[0]
  const formats = cldrData.main[locale]?.dates?.calendars?.gregorian?.dateFormats
  if (formats) {
    for (const key of Object.keys(formats)) {
      if (typeof formats[key] === 'string') {
        formats[key] = formats[key].split(from).join(to)
      }
    }
    logger.debug('patchDateSeparator patched dateFormats:', formats)
  }
  return cldrData
}

// ~9px per character at 14px semi-bold + 32px (minimal padding + sort icon space)
const HEADER_CHAR_WIDTH = 9
const HEADER_PADDING = 32

/**
 * Calculate minimum column width needed to display header text in full.
 * Uses customWidth if specified (even if narrower), otherwise returns calculated minimum.
 * For empty titles (action/checkbox columns), returns customWidth or a default.
 */
export function resolveColumnWidth(
  customWidth: number | string | undefined,
  headerText: string
): number {
  const trimmed = headerText?.trim() ?? ''

  // Action/icon/checkbox columns (empty title) — respect custom width as-is
  if (!trimmed) {
    if (customWidth === undefined) return 50
    const parsed = typeof customWidth === 'string' ? parseInt(customWidth, 10) : customWidth
    return isNaN(parsed) ? 50 : parsed
  }

  // If custom width is specified, use it (even if narrower than calculated minimum)
  if (customWidth !== undefined) {
    const parsed = typeof customWidth === 'string' ? parseInt(customWidth, 10) : customWidth
    if (!isNaN(parsed)) return parsed
  }

  // No custom width - calculate minimum from header text
  return Math.max(80, trimmed.length * HEADER_CHAR_WIDTH + HEADER_PADDING)
}

// Kendo component popup labels (calendar/date/time pickers) are NOT covered by
// CLDR data — they come from Kendo's own message bundle and default to English
// unless registered via loadMessages. These are loaded once centrally in main.ts
// so every picker across the site shows localized popup labels.
const kendoComponentMessages: Record<string, Record<string, string>> = {
  en: {
    'calendar.today': 'Today',
    'datetimepicker.date': 'Date',
    'datetimepicker.time': 'Time',
    'datetimepicker.cancel': 'Cancel',
    'datetimepicker.set': 'Set',
    'timepicker.now': 'Now',
    'timepicker.cancel': 'Cancel',
    'timepicker.set': 'Set',
  },
  de: {
    'calendar.today': 'Heute',
    'datetimepicker.date': 'Datum',
    'datetimepicker.time': 'Uhrzeit',
    'datetimepicker.cancel': 'Abbrechen',
    'datetimepicker.set': 'Übernehmen',
    'timepicker.now': 'Jetzt',
    'timepicker.cancel': 'Abbrechen',
    'timepicker.set': 'Übernehmen',
  },
  fr: {
    'calendar.today': "Aujourd'hui",
    'datetimepicker.date': 'Date',
    'datetimepicker.time': 'Heure',
    'datetimepicker.cancel': 'Annuler',
    'datetimepicker.set': 'Définir',
    'timepicker.now': 'Maintenant',
    'timepicker.cancel': 'Annuler',
    'timepicker.set': 'Définir',
  },
  es: {
    'calendar.today': 'Hoy',
    'datetimepicker.date': 'Fecha',
    'datetimepicker.time': 'Hora',
    'datetimepicker.cancel': 'Cancelar',
    'datetimepicker.set': 'Establecer',
    'timepicker.now': 'Ahora',
    'timepicker.cancel': 'Cancelar',
    'timepicker.set': 'Establecer',
  },
}

// Region variants that LocalizationProvider :language may receive, mapped to base language
const localeVariants: Record<string, string[]> = {
  en: ['en', 'en-US'],
  de: ['de', 'de-DE', 'de-AT'],
  fr: ['fr', 'fr-FR'],
  es: ['es', 'es-ES'],
}

/**
 * Registers Kendo component popup labels (Today, Cancel, Set, Date, Time, Now)
 * for all supported languages. Kendo's loadMessages registry is global, so a
 * single call at startup localizes every date/time picker across the app.
 * Messages are registered under both the base language ('de') and region
 * variants ('de-DE', 'de-AT') to match whatever value a component passes to
 * LocalizationProvider :language.
 */
export function loadKendoComponentMessages() {
  for (const baseLang of Object.keys(kendoComponentMessages)) {
    const messages = kendoComponentMessages[baseLang]
    for (const variant of localeVariants[baseLang] ?? [baseLang]) {
      loadMessages(messages, variant)
    }
  }
  logger.debug('loadKendoComponentMessages: registered picker labels for all locales')
}

function sanitizeNumbersCldr(cldrData: any): any {
  const locale = Object.keys(cldrData.main)[0]
  const numbers = cldrData.main[locale].numbers
  if (numbers) {
    delete numbers['rationalFormats-numberSystem-latn']
  }
  return cldrData
}

